/**
 * Composant ProfileCard
 *
 * Ce composant affiche une carte de profil utilisateur avec diverses informations et options d'interaction.
 * Il est utilisé pour présenter les profils des utilisateurs dans différentes sections de l'application
 * comme la page d'accueil, les favoris, les échanges, etc.
 *
 * Le composant gère l'affichage des détails du profil, des badges (nouveau profil, sponsorisé),
 * des statuts (en ligne, vérifié), et les actions possibles sur un profil
 * (accepter/refuser une demande, voir le profil, etc.).
 *
 * @author Landry
 */

import { InlineIcon } from "@iconify/react";
import {
  Box,
  Button,
  Grid,
  Modal,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Slide,
  Popper,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import "moment/locale/fr";
import "./ProfileCard.styles.scss";
import * as yup from "yup";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { HighlightOffRounded } from "@mui/icons-material";
import {
  TCountry,
  TCurrentExchange,
  TMemberCurrentExchange,
} from "../../types";
import HistoriqueConversation from "./Modals-content/Historique-conversation/HistoriqueConversation";
import { Toastify } from "@/utils/toast";
import { store } from "@/store";
import { apiAcceptDemand, apiRemindDemand } from "../../api/exchange.api";
import {
  updateCurrentExchange,
  updateOpenDiscussion,
} from "@/store/reducers/general_param/general_param.actions";
import { FavoriteContext } from "../../pages/Favorite-content/Favorite.content";
import { BlacklistContext } from "../../pages/Blacklist-content/Blacklist.content";
import { ExchangeContext } from "../../pages/Exchange-content/Exchange.content";
import {
  updateAcceptExchange,
  updateMemberCurrentExchange,
} from "@/store/reducers/member/member.actions";
import TextDisplayer from "@/components/TextDisplayer/TextDisplayer";
import { TMemberProfile } from "@/features/auth/types";
import { selectMemberProfile } from "@/store/reducers/member/member.selector";
import { useSelector } from "react-redux";
import ModalFullProfil from "@/components/Modal-full-profile/ModalFullProfil";
import * as moment from "moment";
import "moment/locale/fr";

/**
 * Interface définissant les propriétés du composant ProfileCard
 *
 * Contient toutes les informations nécessaires pour afficher une carte de profil complète,
 * y compris les informations personnelles, les statuts, et les options d'interaction.
 */
export type TProfileCard = {
  /** ID unique du profil */
  _id?: string;
  /** Indique si la carte est en mode démonstration */
  isDemo?: boolean;
  /** ID de la conversation associée à ce profil */
  conversationId?: string;
  /** Mode d'affichage alternatif */
  alt?: boolean;
  /** Désactive les options de coin */
  no_corner_option?: boolean;
  /** Indique si le profil est vérifié */
  isVerified?: boolean;
  /** Indique si l'utilisateur est en ligne */
  isOnline?: boolean;
  /** Indique s'il y a plus d'informations disponibles */
  hasMore?: boolean;
  /** Nom d'utilisateur */
  username?: string;
  /** Type de la carte (consulté, non consulté, etc.) */
  type?: "consulted" | "non consultés" | "Mes spectateurs" | "";
  /** Pourcentage de complétion du profil */
  progressValue?: number | undefined;
  /** Identifiant du profil (obligatoire) */
  profil_id: string;
  /** Âge de l'utilisateur */
  age?: number;
  /** Ville de résidence */
  town?: string | null;
  /** Pays de résidence */
  country?: string | null;
  /** Origine ethnique/pays d'origine */
  origine?: string | null;
  /** Autre pays de résidence */
  otherHomeCountry?: string | null;
  /** Nationalité */
  nationalite?: string | null;
  /** Statut d'envoi (envoyé, reçu) */
  sendStatut?: "sent" | "received" | "";
  /** Description du profil */
  description?: string;
  /** Description du profil recherché */
  profile_wanted?: string;
  /** Indique si le profil est clôturé */
  isCloture?: boolean;
  /** Indique si l'utilisateur est en échange */
  isExchange?: boolean;
  /** Indique si le profil est un favori mutuel */
  isMutualFavorite?: boolean;
  /** Indique si le profil est simplement en favori */
  isSimpleFavorite?: boolean;
  /** Informations de clôture du profil */
  cloture?: { date_cloture: string; motif: string; emitter: string } | null;
  /** Indique si le profil est en liste noire */
  isListeNoire?: boolean;
  /** ID de la conversation */
  conversation_id?: string;
  /** Indique s'il y a une conversation active */
  has_conversation?: boolean;
  /** Date de création du profil */
  date_of_creation?: string;
  /** Indique si le profil a été supprimé */
  is_deleted?: boolean;
  /** Date de dernière connexion */
  last_connection?: string | null;
  /** Informations de fermeture de l'échange */
  closure?: {
    closedBy: string;
    closedAt: string;
    reason: string | null;
  };
  /** Conversation actuelle de l'utilisateur */
  currentConversation?: TMemberCurrentExchange | null;
  /** Indique si le profil a été visité */
  isVisited?: boolean;
  /** Indique si l'utilisateur est un visiteur */
  isVisitor?: boolean;
  /** Genre de l'utilisateur */
  gender?: "MALE" | "FEMALE";
  /** Fonction pour rafraîchir la page d'accueil */
  refreshHome?: (
    type?:
      | "add-favorite"
      | "remove-favorite"
      | "add-blacklist"
      | "remove-blacklist"
      | "visited"
  ) => void;
  /** Indique si le profil provient des fans */
  fromFan?: boolean;
  /** Indique si l'utilisateur est banni */
  is_banished?: boolean;
  /** Indique si l'utilisateur a un abonnement */
  hasSubscription?: boolean;
  /** Date de création de la demande */
  demandCreatedAt?: string;
  /** Indique si le profil est nouvellement ajouté */
  isNewIncomeProfil?: boolean;
  /** Indique si le profil est sponsorisé */
  isSponsored?: boolean;
  /** Indique si l'utilisateur a déjà été en échange */
  hasAlreadyBeenInExchange?: boolean;
};

/**
 * Composant principal ProfileCard
 *
 * Affiche une carte de profil utilisateur avec toutes les informations pertinentes
 * et les options d'interaction disponibles selon le contexte d'utilisation.
 *
 * @param {TProfileCard} props - Les propriétés du composant
 * @returns {JSX.Element} Composant ProfileCard rendu
 */
const ProfileCard = ({
  _id,
  conversationId,
  isDemo = false,
  alt = false,
  no_corner_option = false,
  isVerified,
  isOnline,
  username,
  type,
  progressValue,
  profil_id,
  age,
  town,
  country,
  origine,
  otherHomeCountry,
  nationalite,
  hasMore,
  sendStatut,
  description,
  profile_wanted,
  isCloture = false,
  isExchange = false,
  isMutualFavorite = false,
  isSimpleFavorite = false,
  cloture = null,
  isListeNoire = false,
  conversation_id = "",
  has_conversation = false,
  date_of_creation = "",
  is_deleted = false,
  last_connection = null,
  closure,
  currentConversation,
  isVisited,
  isVisitor,
  gender,
  refreshHome,
  fromFan,
  is_banished = false,
  hasSubscription,
  demandCreatedAt,
  isNewIncomeProfil,
  hasAlreadyBeenInExchange,
  isSponsored,
}: TProfileCard) => {
  // Récupération du profil membre depuis le store Redux
  const memberProfileFromStore = useSelector(
    selectMemberProfile
  ) as TMemberProfile;

  // ===== États pour la gestion des modales =====

  /** État pour l'ouverture/fermeture de la modal du profil complet */
  const [openFullProfil, setOpenFullProfil] = useState(false);

  /** États pour les différentes modales de gestion des demandes et actions */
  const [openDenyRequest, setOpenDenyRequest] = useState(false);
  const [openConfirmedDenyRequest, setOpenConfirmedDenyRequest] =
    useState(false);
  const [openRemoveFavorite, setOpenRemoveFavorite] = useState(false);
  const [openRemoveBlacklist, setOpenRemoveBlacklist] = useState(false);

  /** États pour les chargements des différentes actions */
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loadingRemind, setLoadingRemind] = useState(false);

  /** État pour l'ouverture/fermeture de la modal d'historique de conversation */
  const [openModal, setOpenModal] = useState(false);

  /**
   * Ouvre la modal d'historique de conversation
   */
  const handleOpenModal = () => setOpenModal(true);

  /**
   * Ferme la modal d'historique de conversation
   */
  const handleCloseModal = () => setOpenModal(false);

  /**
   * Ouvre la modal du profil complet
   */
  const handleOpenFullProfil = () => setOpenFullProfil(true);

  /**
   * Ferme la modal du profil complet
   */
  const handleCloseFullProfil = () => setOpenFullProfil(false);

  /**
   * Ouvre la modal de refus de demande
   */
  const handleOpenDenyRequest = () => setOpenDenyRequest(true);

  /**
   * Ferme la modal de refus de demande et réinitialise le formulaire
   */
  const handleCloseDenyRequest = () => {
    formikDenyRequest.resetForm();
    setOpenDenyRequest(false);
  };

  /**
   * Ouvre la modal de confirmation de refus de demande
   */
  const handleOpenConfirmedDenyRequest = () =>
    setOpenConfirmedDenyRequest(true);

  /**
   * Ferme la modal de confirmation de refus de demande
   */
  const handleCloseConfirmedDenyRequest = () =>
    setOpenConfirmedDenyRequest(false);

  /**
   * Ouvre la modal de suppression des favoris
   */
  const handleOpenRemoveFavorite = () => setOpenRemoveFavorite(true);

  /**
   * Ferme la modal de suppression des favoris
   */
  const handleCloseRemoveFavorite = () => setOpenRemoveFavorite(false);

  /**
   * Ouvre la modal de suppression de la liste noire
   */
  const handleOpenRemoveBlacklist = () => setOpenRemoveBlacklist(true);

  /**
   * Ferme la modal de suppression de la liste noire
   */
  const handleCloseRemoveBlacklist = () => setOpenRemoveBlacklist(false);

  /**
   * Schéma de validation pour le formulaire de refus de demande
   */
  const validationSchema = yup.object({
    motif: yup.string().required(" la raison est obligatoire "),
  });

  /**
   * Configuration du formulaire pour le refus de demande
   */
  const formikDenyRequest = useFormik({
    initialValues: {
      motif: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        handleOpenConfirmedDenyRequest();
      }, 500);
    },
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Récupération des fonctions depuis les contextes
  const { removeFromBlacklistFct, loadingRemoveB } =
    useContext(BlacklistContext);

  const { removeFromFavoriteFct, loadingRemove } = useContext(FavoriteContext);

  const {
    acceptExchangeFct,
    loadingAccept,
    rejectDemandFct,
    loadingReject,
    cancelDemandFct,
    loadingCancel,
  } = useContext(ExchangeContext);

  /**
   * Fonction pour relancer une demande d'échange
   * Envoie une notification à l'autre utilisateur
   */
  const remindExchange = () => {
    setLoadingRemind(true);

    apiRemindDemand(_id || "")
      .then((response: any) => {
        setLoadingRemind(false);
        console.log(response);
        Toastify(
          ` La relance a bien été effectuée auprès de ${username} `,
          "success",
          5000
        );
      })
      .catch((error) => {
        setLoadingRemind(false);
        console.log(error);
        error?.response?.data?.message &&
          Toastify(`${error?.response?.data?.message}`, "error", 5000);
      });
  };

  /**
   * État pour le popper qui affiche plus d'informations sur le profil
   */
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  /**
   * Gère l'événement d'entrée de la souris pour afficher le popper
   */
  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  /**
   * Gère l'événement de sortie de la souris pour masquer le popper
   */
  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(null);
  };

  const openProfilePopper = Boolean(anchorEl);
  const id = openProfilePopper ? "profile-card-popper" : undefined;

  /**
   * Récupère le code pays alpha-2 à partir du nom du pays
   * Utilisé pour afficher les drapeaux des pays
   *
   * @param {string} countryName - Nom du pays
   * @returns {string|null} Code alpha-2 du pays ou null si non trouvé
   */
  const getAlpha2CountryCode = (countryName: string): string | null => {
    try {
      const countries: TCountry[] = require("@/assets/json/countries.json");

      // Fonction pour normaliser les chaînes (supprimer les accents, mettre en minuscule)
      const normalizeString = (str: string) =>
        str
          .trim()
          .toLowerCase()
          .replace(/'|'/g, "'")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, ""); // Remove accents

      const normalizedCountry = normalizeString(countryName);

      const found = countries.find(
        (item) => normalizeString(item.country) === normalizedCountry
      );

      return found ? found.abbreviation : null;
    } catch (error) {
      console.error("Erreur lors de la récupération du code alpha2 :", error);
      return null;
    }
  };

  return (
    <>
      {/* Conteneur principal de la carte de profil */}
      <div
        className={`card-profile-container ${isSponsored ? "sponsored-profile " : isNewIncomeProfil ? "new-profile" : ""} `}
        // onClick={handleOpenFullProfil}
      >
        <div className="card-profile">
          {/* Indicateur de visite (désactivé) */}
          {false && (
            <div className="visited-indicator">
              {isVisited && <div className="visited" />}
              {isVisitor && <div className={`visitor ${gender} `} />}
            </div>
          )}

          {/* Indicateurs textuels (profil sponsorisé, nouveau profil, profil visité, visiteur) */}
          <div className="visited-indicator-text">
            {/* Badge "Sponsorisé" */}
            {isSponsored && (
              <div className="sponsor-badge-icon">
                <InlineIcon icon="ic:twotone-rocket-launch" />
                <span>Sponsorisé</span>
              </div>
            )}

            {/* Badge "Nouveau" */}
            {isNewIncomeProfil && (
              <div className="new-badge">
                <span> Nouveau </span>
              </div>
            )}

            {/* Indicateur "J'ai consulté" */}
            {isVisited && (
              <span className="visited"> J'ai consulté son profil </span>
            )}

            {/* Indicateur "Il/Elle a consulté" */}
            {isVisitor && (
              <span
                className={`visitor ${gender === "MALE" ? "male" : "female"} `}
              >
                {" "}
                {gender === "MALE" ? "Il a " : "Elle a"} consulté mon profil{" "}
              </span>
            )}

            {/* Date de création de la demande */}
            {demandCreatedAt && (
              <span className="demand-date-creation">
                {" "}
                Depuis le :{" "}
                <b>
                  {moment.default(demandCreatedAt).format("LLL") || ""}
                </b>{" "}
              </span>
            )}
          </div>

          {/* En-tête de la carte (drapeaux, ID, origine, nationalité, lieu) */}
          <>
            <div className={`card-profile__header`}>
              {/* Partie gauche avec pastille/drapeau et ID */}
              <div className="pastille-part">
                <div className={`pastille ${isVerified ? "" : "ko"} `}>
                  {/* Affichage du drapeau d'origine ou de l'icône de vérification */}
                  {origine ? (
                    <InlineIcon
                      style={{ borderRadius: "50%" }}
                      icon={`flag:${getAlpha2CountryCode(origine)?.toLowerCase()}-1x1`}
                    />
                  ) : (
                    <InlineIcon
                      style={{ borderRadius: "50%" }}
                      icon={`${isVerified ? "qlementine-icons:certified-16" : "icon-park-outline:close-small"}`}
                    />
                  )}

                  {/* Affichage du drapeau du second pays si présent */}
                  {otherHomeCountry && (
                    <div
                      className={`other-country-flag ${isVerified ? "" : "ko"} `}
                    >
                      <InlineIcon
                        style={{ borderRadius: "50%" }}
                        icon={`flag:${getAlpha2CountryCode(otherHomeCountry || "")?.toLowerCase() || "unknown"}-1x1`}
                      />
                    </div>
                  )}
                </div>
                <p className="profil-id">ID: {profil_id} </p>
              </div>

              {/* Partie droite avec informations supplémentaires (origine, nationalité, lieu) */}
              <div className="tag-info-part">
                {/* Information d'origine */}
                <div className="tag">
                  <InlineIcon icon="el:globe" />
                  {origine ? (
                    <p>
                      <span>Origine</span> <b title={origine}>{origine}</b>
                    </p>
                  ) : (
                    <p> -- </p>
                  )}
                </div>

                {/* Information de nationalité */}
                <div className="tag">
                  <InlineIcon icon="icon-park-outline:passport" />
                  {nationalite ? (
                    <p>
                      <span>Nationalité</span>{" "}
                      <b title={nationalite}>{nationalite}</b>{" "}
                    </p>
                  ) : (
                    <p> -- </p>
                  )}
                </div>

                {/* Information de lieu de résidence */}
                <div className="tag">
                  <InlineIcon icon="akar-icons:location" />
                  <p title={town + "•" + country}>
                    {town ? town : "--"} , {country ? country : "--"}
                  </p>
                </div>
              </div>
            </div>
          </>

          {/* Partie centrale avec nom d'utilisateur, âge et indicateur de présence */}
          <>
            <div className="card-profile__middle">
              <div className="profil-infos-username-age">
                {/* Indicateur de favori mutuel */}
                {isMutualFavorite && (
                  <InlineIcon
                    icon="iconamoon:star-fill"
                    style={{ color: "orange", marginRight: "10px" }}
                  />
                )}

                {/* Indicateur de favori simple */}
                {isSimpleFavorite && !isMutualFavorite && (
                  <InlineIcon
                    icon="iconamoon:star-duotone"
                    style={{ color: "orange", marginRight: "10px" }}
                  />
                )}

                {/* Nom d'utilisateur et âge */}
                <div className="username-age">
                  <p className="username" title={username}>
                    {username}
                  </p>
                  <p className="age">
                    | {age}
                    <small>ans</small>{" "}
                  </p>
                </div>

                {/* Indicateur de présence (en ligne/hors ligne) */}
                <div className={`dot  ${isOnline ? "" : "offline"}`}></div>
              </div>

              {/* Pourcentage de complétion du profil */}
              <p
                className={`profil-progress-text ${progressValue! <= 30 ? "bad" : progressValue! <= 70 ? "middle" : ""}`}
              >
                Profil rempli à <b>{progressValue}%</b>{" "}
              </p>
            </div>
          </>

          {/* Partie des détails du profil (description, statuts spéciaux) */}
          <>
            {closure === undefined && (
              <div
                className="card-profile__detail-part"
                onMouseLeave={handleMouseLeave}
              >
                {/* Description du profil avec popper pour afficher le texte complet */}
                <div
                  aria-describedby={id}
                  // onMouseEnter={handleMouseEnter}
                  className="detail-text-part"
                  contentEditable={false}
                >
                  {description || ""}
                </div>
                <Popper
                  id={id}
                  open={openProfilePopper}
                  anchorEl={anchorEl}
                  style={{ zIndex: 10 }}
                  onMouseLeave={handleMouseLeave}
                >
                  <TextDisplayer
                    className="tooltip-live-note"
                    value={description || ""}
                    isForPopper
                  />
                </Popper>

                {/* Affichage des statuts spéciaux */}

                {/* Si l'utilisateur est déjà en échange */}
                {currentConversation !== null && (
                  <p
                    className="detail-text-part"
                    style={{ color: "var(--ui-red)", marginTop: "10px" }}
                  >
                    {" "}
                    <InlineIcon icon="fluent-emoji-high-contrast:handshake" />{" "}
                    &nbsp;&nbsp;
                    <b>Cet utilisateur est déjà en échange</b>{" "}
                  </p>
                )}

                {/* Si l'utilisateur a supprimé son compte */}
                {is_deleted && (
                  <p
                    className="detail-text-part"
                    style={{ color: "var(--ui-red)", marginTop: "10px" }}
                  >
                    {" "}
                    <InlineIcon icon="streamline:interface-user-delete-actions-close-delete-deny-fail-geometric-human-person-remove-single-up-user" />{" "}
                    &nbsp;&nbsp;<b>Cet utilisateur a supprimé son compte</b>{" "}
                  </p>
                )}

                {/* Si l'utilisateur est banni */}
                {is_banished && (
                  <p
                    className="detail-text-part"
                    style={{ color: "var(--ui-red)", marginTop: "10px" }}
                  >
                    {" "}
                    <InlineIcon icon="tdesign:user-blocked" /> &nbsp;&nbsp;
                    <b>Cet utilisateur est banni.</b>{" "}
                  </p>
                )}

                {/* Si une demande a été envoyée */}
                {sendStatut === "sent" && !isCloture && (
                  <p
                    className="detail-text-part"
                    style={{ color: "var(--ui-blue)", marginTop: "10px" }}
                  >
                    {" "}
                    <InlineIcon icon="carbon:mail-reply" />
                    &nbsp;&nbsp;<b>Demande envoyée</b>{" "}
                  </p>
                )}

                {/* Si une demande a été reçue */}
                {sendStatut === "received" && !isCloture && (
                  <p
                    className="detail-text-part"
                    style={{ color: "var(--ui-red)", marginTop: "10px" }}
                  >
                    <InlineIcon icon="carbon:mail-reply" flip="horizontal" />
                    &nbsp;&nbsp;<b>Demande reçue</b>{" "}
                  </p>
                )}
              </div>
            )}
          </>

          {/* Partie affichant les informations de clôture si l'échange est fermé */}
          <>
            {closure !== undefined && (
              <div className="card-profile__cloture-part">
                <p className="cloture-text">
                  {" "}
                  Échange clôturé par &nbsp;<b>{closure?.closedBy || "yel"}</b>
                  &nbsp; pour le motif : <br />
                  <b>{closure?.reason || "***"}</b>&nbsp;{" "}
                </p>
                <small className="cloture-text-small">
                  {" "}
                  Échange fermé le
                  <b>
                    {" "}
                    {moment.default(closure?.closedAt).format("LLL") ||
                      "01/12/2024"}{" "}
                  </b>{" "}
                </small>
              </div>
            )}
          </>

          {/* Partie inférieure avec les actions disponibles */}
          <div className="card-profile__bottom-part">
            {/* Actions pour une demande reçue */}
            {sendStatut === "received" && closure === undefined && (
              <>
                {/* Bouton pour refuser la demande */}
                <Chip
                  color="error"
                  variant="outlined"
                  size="medium"
                  label="Refuser"
                  icon={
                    <InlineIcon
                      icon={
                        loadingReject
                          ? "eos-icons:three-dots-loading"
                          : "mingcute:close-fill"
                      }
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    !loadingReject && handleOpenDenyRequest();
                  }}
                />

                {/* Bouton pour accepter la demande */}
                <Chip
                  color="success"
                  variant="filled"
                  size="medium"
                  label="Accepter"
                  disabled={
                    !!memberProfileFromStore?.currentConversation ||
                    currentConversation !== null
                  }
                  icon={
                    <InlineIcon
                      icon={
                        loadingAccept
                          ? "eos-icons:three-dots-loading"
                          : "mingcute:check-fill"
                      }
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    !loadingAccept &&
                      acceptExchangeFct(conversationId || "", username!);
                  }}
                />
              </>
            )}

            {/* Actions pour une demande envoyée */}
            {sendStatut === "sent" && closure === undefined && (
              <>
                {/* Bouton pour annuler la demande */}
                <Chip
                  color="error"
                  variant="outlined"
                  size="medium"
                  label="Annuler"
                  icon={
                    <InlineIcon
                      icon={
                        loadingCancel
                          ? "eos-icons:three-dots-loading"
                          : "mingcute:close-fill"
                      }
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    !loadingCancel &&
                      cancelDemandFct(conversationId || "", username!);
                  }}
                />

                {/* Bouton pour relancer la demande */}
                <Chip
                  color="info"
                  variant="filled"
                  size="medium"
                  label="Relancer"
                  disabled={
                    !!memberProfileFromStore?.currentConversation ||
                    currentConversation !== null
                  }
                  icon={
                    <InlineIcon
                      icon={
                        loadingRemind
                          ? "eos-icons:three-dots-loading"
                          : "mingcute:check-fill"
                      }
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    !loadingRemind && remindExchange();
                  }}
                />
              </>
            )}

            {/* Bouton pour voir l'historique des conversations si l'échange est clôturé */}
            {closure !== undefined && (
              <Chip
                color="primary"
                variant="filled"
                size="medium"
                label="Historique des conversations"
                icon={<InlineIcon icon="ic:round-history" />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleOpenModal();
                }}
              />
            )}

            {/* Bouton pour voir le profil complet si l'utilisateur n'est pas en liste noire */}
            {!isListeNoire && (
              <Chip
                color="primary"
                variant="outlined"
                className="chip-voir-profil"
                size="medium"
                label="Voir le profil"
                icon={<InlineIcon icon="iconamoon:profile-fill" />}
                onClick={() => {
                  !isDemo && handleOpenFullProfil();
                }}
              />
            )}

            {/* Actions supplémentaires pour le mode alternatif */}
            {alt && (
              <>
                {isListeNoire ? (
                  <>
                    {/* Bouton pour retirer de la liste noire */}
                    <Chip
                      color="primary"
                      variant="outlined"
                      size="medium"
                      label="Retirer de la liste noire"
                      icon={<InlineIcon icon="tabler:user-x" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleOpenRemoveBlacklist();
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* Bouton pour retirer de la liste des favoris */}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleOpenRemoveFavorite();
                      }}
                    >
                      <InlineIcon icon="mingcute:delete-fill" />
                    </IconButton>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Les différentes modales et dialogues du composant */}

      {/* Modal pour afficher le profil complet */}
      {openFullProfil && (
        <ModalFullProfil
          _id={_id}
          openModal={openFullProfil}
          closeModal={handleCloseFullProfil}
          isOnline={isOnline}
          isVerified={isVerified}
          hasMore={hasMore}
          username={username}
          type={type}
          age={age}
          town={town}
          country={country}
          origine={origine}
          profil_id={profil_id}
          nationalite={nationalite}
          progressValue={progressValue}
          date_of_creation={date_of_creation}
          last_connection={last_connection}
          sendStatut={sendStatut}
          isSimpleFavorite={isSimpleFavorite}
          isMutualFavorite={isMutualFavorite}
          isListeNoire={isListeNoire}
          refreshHome={refreshHome}
          fromFan={fromFan}
          hasSubscription={hasSubscription}
          isNewIncomeProfil={isNewIncomeProfil}
          hasAlreadyBeenInExchange={hasAlreadyBeenInExchange}
        />
      )}

      {/* Modal pour refuser une demande d'échange */}
      {openDenyRequest && (
        <Modal
          open={openDenyRequest}
          onClose={handleCloseDenyRequest}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <form
            className="form-raison-part"
            onSubmit={formikDenyRequest.handleSubmit}
          >
            <IconButton
              className="close-icon"
              size="large"
              color="inherit"
              onClick={handleCloseDenyRequest}
            >
              <HighlightOffRounded />
            </IconButton>
            <TextField
              placeholder="La raison du refus"
              variant="outlined"
              label="Raison"
              name="motif"
              onChange={formikDenyRequest.handleChange}
              value={formikDenyRequest.values.motif}
              error={
                formikDenyRequest.touched.motif &&
                Boolean(formikDenyRequest.errors.motif)
              }
              helperText={
                formikDenyRequest.touched.motif &&
                (formikDenyRequest.errors.motif as string)
              }
              type="text"
            />

            <Box sx={{ width: "100%" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} lg={6}>
                  <Button
                    variant="contained"
                    color="tertiary"
                    onClick={handleCloseDenyRequest}
                    sx={{ marginTop: "30px", width: "100%" }}
                    type="submit"
                  >
                    {" "}
                    ANNULER{" "}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} lg={6}>
                  <LoadingButton
                    loading={loading}
                    loadingPosition="center"
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: "30px", width: "100%" }}
                    type="submit"
                    disabled={!formikDenyRequest.isValid}
                  >
                    {" "}
                    REFUSER{" "}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Modal>
      )}

      {/* Dialogue de confirmation pour refuser une demande d'échange */}
      {openConfirmedDenyRequest && (
        <Dialog
          open={openConfirmedDenyRequest}
          onClose={handleCloseConfirmedDenyRequest}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Refus de demande"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <b>Attention!!!</b> cette action est irréversible.
              <br />
              Vous êtes sur le point de <b>refuser</b> la demande d'échange de
              ce profil. Êtes-vous sûr(e) de vouloir le faire ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmedDenyRequest}>Annuler</Button>
            <LoadingButton
              id="id-signal-btn"
              loading={loading2}
              loadingPosition="center"
              onClick={() => {
                rejectDemandFct(
                  conversationId || "",
                  username!,
                  formikDenyRequest.values.motif
                );
                handleCloseDenyRequest();
                handleCloseConfirmedDenyRequest();
              }}
              autoFocus
            >
              Refuser
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialogue de confirmation pour retirer un profil des favoris */}
      {openRemoveFavorite && (
        <Dialog
          open={openRemoveFavorite}
          onClose={handleCloseRemoveFavorite}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Retirer des favoris"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {/* <b>Attention!!!</b> cette action est déliquate.<br /> */}
              Vous êtes sur le point de <b> retirer </b> ce profil de vos
              favoris. Êtes-vous sûr(e) de vouloir le faire ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemoveFavorite}>Annuler</Button>
            <LoadingButton
              loading={loadingRemove}
              loadingPosition="center"
              variant="contained"
              color="primary"
              id="id-remove-favorite-btn"
              onClick={() => {
                removeFromFavoriteFct(_id || "", username!);
                handleCloseRemoveFavorite();
              }}
              type="submit"
            >
              {" "}
              Retirer{" "}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialogue de confirmation pour retirer un profil de la liste noire */}
      {openRemoveBlacklist && (
        <Dialog
          open={openRemoveBlacklist}
          onClose={handleCloseRemoveBlacklist}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Retirer de la liste noire"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {/* <b>Attention!!!</b> cette action est déliquate.<br /> */}
              Vous êtes sur le point de <b> retirer </b> ce profil de votre
              liste noire. Êtes-vous sûr(e) de vouloir le faire ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemoveBlacklist}>Annuler</Button>
            <LoadingButton
              loading={loadingRemoveB}
              loadingPosition="center"
              variant="contained"
              color="primary"
              id="id-remove-blacklist-btn"
              onClick={() => {
                removeFromBlacklistFct(_id || "", username!);
                handleCloseRemoveBlacklist();
              }}
              type="submit"
            >
              {" "}
              Retirer{" "}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal pour afficher l'historique des conversations */}
      {openModal && (
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Slide direction="left" in={openModal} mountOnEnter unmountOnExit>
            <div className="modal-historique-container">
              {/* En-tête de la modal */}
              <div className="modal-historique-container__title-part">
                <IconButton
                  className="back-icon"
                  size="medium"
                  color="secondary"
                  onClick={handleCloseModal}
                >
                  <InlineIcon icon="tabler:chevron-left" />
                </IconButton>
                <div className="title-infos-username-age">
                  <div className="username-age">
                    <p className="username">Historique de conversation</p>
                  </div>
                </div>
                <IconButton
                  className="close-icon"
                  size="medium"
                  color="secondary"
                  onClick={handleCloseModal}
                >
                  <InlineIcon icon="iconamoon:close-bold" />
                </IconButton>
              </div>
              {/* Corps de la modal avec le composant d'historique */}
              <div className="modal-historique-container__body-part">
                <HistoriqueConversation
                  closeModal={handleCloseModal}
                  conversation_id={conversationId || ""}
                />
              </div>
            </div>
          </Slide>
        </Modal>
      )}
    </>
  );
};

/**
 * Exporte le composant ProfileCard comme composant par défaut
 * Ce composant est utilisé dans plusieurs sections de l'application pour
 * afficher les cartes de profil des utilisateurs avec diverses options
 * d'interaction basées sur le contexte.
 */
export default ProfileCard;
